import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Identifier, sendDeletionOtp, errorKey } from "../../api/accountDeletion";
import { errorText, inputClass, linkButton, primaryButton } from "./styles";

// Accepts 5XXXXXXXX, 05XXXXXXXX or 9715XXXXXXXX and returns the 971XXXXXXXXX form the API expects
const normalizePhone = (value: string) => {
    let digits = value.replace(/\D/g, "");
    if (digits.startsWith("00971")) digits = digits.slice(2);
    if (digits.startsWith("971")) return digits;
    if (digits.startsWith("0")) digits = digits.slice(1);
    return "971" + digits;
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Props = {
    onSent: (identifier: Identifier, display: string) => void;
};

const IdentifyStep: React.FC<Props> = ({ onSent }) => {
    const { t } = useTranslation();
    const [mode, setMode] = useState<"phone" | "email">("phone");
    const [value, setValue] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const toggleMode = () => {
        setMode(mode === "phone" ? "email" : "phone");
        setValue("");
        setError(null);
    };

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        const phone = normalizePhone(value);
        const email = value.trim();
        if (mode === "phone" ? !/^971\d{9}$/.test(phone) : !EMAIL_REGEX.test(email)) {
            setError(t(mode === "phone" ? "delete_account.invalid_phone" : "delete_account.invalid_email"));
            return;
        }

        const identifier: Identifier = mode === "phone" ? { phone } : { email };
        setBusy(true);
        try {
            await sendDeletionOtp(identifier);
            onSent(identifier, mode === "phone" ? `+${phone}` : email);
        } catch (err) {
            setError(t(`delete_account.errors.${errorKey(err, "generic")}`));
        } finally {
            setBusy(false);
        }
    };

    return (
        <form onSubmit={onSubmit} noValidate className="space-y-4">
            <label className="block">
                <span className="mb-2 block text-sm font-semibold text-gray-700">
                    {t(mode === "phone" ? "delete_account.phone_label" : "delete_account.email_label")}
                </span>
                {mode === "phone" ? (
                    <div dir="ltr" className="flex items-stretch gap-2">
                        <span className="flex items-center rounded-xl border border-gray-300 bg-gray-50 px-4 text-gray-700">
                            +971
                        </span>
                        <input
                            type="tel"
                            inputMode="numeric"
                            autoComplete="tel-national"
                            placeholder={t("delete_account.phone_placeholder")}
                            className={inputClass}
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                        />
                    </div>
                ) : (
                    <input
                        dir="ltr"
                        type="email"
                        autoComplete="email"
                        placeholder={t("delete_account.email_placeholder")}
                        className={inputClass}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                    />
                )}
            </label>

            <button type="button" onClick={toggleMode} className={linkButton}>
                {t(mode === "phone" ? "delete_account.use_email" : "delete_account.use_phone")}
            </button>

            {error && <p className={errorText}>{error}</p>}

            <button type="submit" disabled={busy || !value.trim()} className={primaryButton}>
                {t("delete_account.send_code")}
            </button>
        </form>
    );
};

export default IdentifyStep;
